import { useChartTitle, useReportId } from 'hooks/helperHooks';
import { useCallback, useEffect, useMemo, useState } from 'react';

import PitcherSessionConfig from './PitcherSessionConfig';
import PitcherSessionSearchCriteria from './PitcherSessionSearchCriteria';
import ChartSidebarSummary from 'components/Report/ReportShelf/ChartSidebarSummary';
import PlayerReportHeader from 'components/Report/ReportPageHeader/PlayerReportHeader';
import ReportBar from 'components/Report/ReportBar/ReportBar';
import ReportPage from 'components/Report/ReportPage/ReportPage';
import ReportShelf from 'components/Report/ReportShelf/ReportShelf';
import { Typography } from '@trackman/web-shared-components';
import { chartIdTochartSetup } from 'utilities/chartIdToComponent';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import usePitcherSessionReport from 'hooks/SessionReport/Pitcher/usePitcherSessionReport';
import { useTranslation } from 'react-i18next';
import LoadingModal from 'components/Basic/LoadingModal/LoadingModal';
import { useChartVisibility } from 'hooks/useChartVisibility';
import { useChartBrokerStore } from 'stores/ChartBrokerStore';
import { displayDate } from 'utilities/dateHelpers';

const SessionReportPitcherPage = () => {
  const { isCreate } = useReportId();

  const { t } = useTranslation();

  const {
    isEditMode,
    setIsEditMode,

    searchCriteria,
    searchChips,

    layout,
    tempLayout,
    chartData,

    isLoadingReport,
    generateReport,
    getNewData,
    setTempLayout,

    saveLayoutForSession,
    saveLayoutForOrg,

    resetStore,
    editingChart,
    setEditingChart,

    hasUnsavedEdits,
    discardLayoutChanges,
  } = usePitcherSessionReport();

  const [openSection, setOpenSection] = useState<'searchCriteria' | 'config'>(isCreate ? 'searchCriteria' : 'config');
  const [parent] = useAutoAnimate();

  const downloadName = useMemo(() => {
    const { playerNames = [], seasonNames = [], dateRanges = [] } = searchChips;
    const playerStr = playerNames.length === 1 ? playerNames[0] : `${playerNames.length} players`;
    const seasonStr = seasonNames.length === 0 ? '' : seasonNames.length === 1 ? seasonNames[0] : `${seasonNames.length} seasons`;
    const dateStr = dateRanges.length === 0 ? '' : `${displayDate(dateRanges[0].utcDateFrom)} - ${displayDate(dateRanges[0].utcDateTo)}`;
    return `Pitcher Session Report - ${playerStr} - ${seasonStr || dateStr}`;
  }, [searchChips.playerNames, searchChips.seasonNames, searchChips.dateRanges]);

  const { reset: resetChartBrokerStore } = useChartBrokerStore();

  useEffect(() => {
    return () => {
      resetStore();
      resetChartBrokerStore();
    };
  }, [resetStore, resetChartBrokerStore]);

  const { getChartTitle, getChartSidebarSubTitle } = useChartTitle();

  const { visibleCharts, hiddenCharts, toggleChartVisibility } = useChartVisibility(tempLayout, setTempLayout);

  const handleGenerate = useCallback(() => {
    setOpenSection('config');
    generateReport();
  }, [generateReport]);

  const handleToggleVisibility = useCallback(
    (chartId: string) => {
      toggleChartVisibility(chartId);
    },
    [toggleChartVisibility]
  );

  return (
    <>
      <ReportBar
        reportName={t('SessionReviewReport.PitcherAggregated')}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        downloadName={downloadName}
      />
      <div className='report-layout-content'>
        <ReportShelf
          onGenerate={handleGenerate}
          onModify={getNewData}
          isLoadingReport={isLoadingReport}
          disableGenerate={isLoadingReport || !searchCriteria.players || searchCriteria.players.length === 0}
          editingChart={editingChart}
          setEditingChart={setEditingChart}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          saveLayoutForSession={saveLayoutForSession}
          saveLayoutForOrg={saveLayoutForOrg}
          hasUnsavedEdits={hasUnsavedEdits}
          discardLayoutChanges={discardLayoutChanges}
        >
          <div className='report-layout-shelf' ref={parent}>
            {tempLayout && isEditMode ? (
              <>
                <div className='report-layout-shelf-section'>
                  {!editingChart && (
                    <Typography component='div' variant='body bold'>
                      {t('Report elements')}
                    </Typography>
                  )}
                  {visibleCharts.map((config) => {
                    const { chartId, chartDrawingInfo, ...chartConfig } = config;
                    const { Sidebar } = chartIdTochartSetup[chartId];
                    if (!editingChart) {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={Sidebar ? setEditingChart : undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartSidebarSubTitle(config.chartId, config)}
                          isHidden={config.isHidden ?? false}
                        />
                      );
                    }
                    return Sidebar && editingChart === chartId ? (
                      <Sidebar chartConfig={chartConfig as never} setTempLayout={setTempLayout as any} tempLayout={tempLayout as any} />
                    ) : null;
                  })}
                </div>
                {!editingChart && hiddenCharts.length > 0 && (
                  <div className='report-layout-shelf-section'>
                    <Typography component='div' variant='body bold'>
                      {t('Extra elements')}
                    </Typography>
                    {hiddenCharts.map((config) => {
                      return (
                        <ChartSidebarSummary
                          key={config.chartId}
                          chartId={config.chartId}
                          onEditClick={undefined}
                          onToggleVisibility={handleToggleVisibility}
                          chartLabel={getChartTitle(config.chartId, config)}
                          subtitle={getChartSidebarSubTitle(config.chartId, config)}
                          isHidden={true}
                        />
                      );
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <PitcherSessionSearchCriteria
                  isSearchCriteriaOpen={openSection === 'searchCriteria'}
                  setSearchCriteriaOpen={() => setOpenSection('searchCriteria')}
                />
                <PitcherSessionConfig isConfigOpen={openSection === 'config'} setConfigOpen={() => setOpenSection('config')} />
              </>
            )}
          </div>
        </ReportShelf>
        <div className='report-layout-grid-container'>
          {(chartData ?? []).map((chart, i) => (
            <ReportPage
              key={i}
              layout={layout}
              tempLayout={tempLayout}
              page={i + 1}
              header={
                <PlayerReportHeader
                  playerName={chart.playerName}
                  teamName={searchChips.teamName}
                  seasonNames={searchChips.seasonNames}
                  dateRanges={searchChips.dateRanges}
                />
              }
              pageChartData={chart}
              isEditMode={isEditMode}
              isLoadingData={isLoadingReport}
              setTempLayout={setTempLayout}
              setEditingChart={setEditingChart}
              editingChart={editingChart}
            />
          ))}
          {(chartData == null || chartData.length === 0) && (
            <ReportPage
              layout={layout}
              tempLayout={tempLayout}
              page={1}
              header={<PlayerReportHeader />}
              pageChartData={undefined}
              isEditMode={isEditMode}
              isLoadingData={isLoadingReport}
              setTempLayout={setTempLayout}
              setEditingChart={setEditingChart}
              editingChart={editingChart}
            />
          )}
        </div>
      </div>

      <LoadingModal
        isLoading={isLoadingReport}
        title={isCreate ? t('LoadingModal.title.creating') : t('LoadingModal.title.loading')}
        message={t('LoadingModal.message')}
      />
    </>
  );
};

export default SessionReportPitcherPage;
